import { Component } from 'react';
import { Button, Card, CardBody, CardGroup, Col, Container, Form, Row } from 'reactstrap';
import { connect } from 'react-redux';
import InputGroup from '../../../elements/Forms/InputGroup';
import * as actions from '../../../actions';
import { Field, reduxForm, InjectedFormProps } from 'redux-form';
import approve from 'approvejs';
import history from '../../../helpers/history';
import { sample } from 'lodash';
import { getTranslationKey } from '../../../modules/utils/helpers';
import type { Notify } from '../../../typings/redux';

interface FieldConfig {
  type: string;
  placeholder: string;
  title: string;
  rules: {
    email?: { message: string };
  };
}

const fields: Record<string, FieldConfig> = {
  email: {
    type: 'text',
    placeholder: getTranslationKey('emailPlaceholder'),
    title: getTranslationKey('email'),
    rules: {
      email: {
        message: getTranslationKey('emailErrorMessage')
      }
    }
  }
};

interface FormValues {
  email: string;
}

interface Auth {
  authenticated: boolean;
}

interface OwnProps {
  auth: Auth;
  forgotPassword: (email: string, notify: Notify) => void;
  notify: Notify;
}

type ForgotPasswordProps = OwnProps & InjectedFormProps<FormValues, OwnProps>;

interface ForgotPasswordState {
  backgroundImage: string | undefined;
}

class ForgotPassword extends Component<ForgotPasswordProps, ForgotPasswordState> {
  constructor(props: ForgotPasswordProps) {
    super(props);
    this.submit = this.submit.bind(this);

    this.state = {
      backgroundImage: this.getRandomBackgroundUrl()
    };
  }

  componentDidMount(): void {
    if (this.props.auth.authenticated) history.push('/');
  }

  getRandomBackgroundUrl(): string | undefined {
    const backgroundUrls = [
      `${process.env.REACT_APP_PUBLIC_BUCKET_URL}/builder/login/flying-camera.jpg'`,
      `${process.env.REACT_APP_PUBLIC_BUCKET_URL}/builder/login/camera-smartphone-laptop.jpg'`,
      `${process.env.REACT_APP_PUBLIC_BUCKET_URL}/builder/login/workspace.jpg`
    ];

    return sample(backgroundUrls);
  }

  submit(values: FormValues): void {
    this.props.forgotPassword(values.email, this.props.notify);
  }

  render(): JSX.Element {
    return (
      <div
        className="app flex-row align-items-center"
        style={{
          backgroundImage:
            window.USER_TYPE !== 'VST'
              ? `url(${this.state.backgroundImage})`
              : `url(${window.CDN_URL}/public/gallerypix-loginscreen.jpg)`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundColor: window.USER_TYPE !== 'VST' ? 'gray' : 'unset'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md="7">
              <CardGroup>
                <Card>
                  <CardBody>
                    <Form onSubmit={this.props.handleSubmit(this.submit)}>
                      <h1> {getTranslationKey('forgotPasswordTitle')}</h1>
                      <p className="text-muted">{getTranslationKey('forgotPasswordTitleDesc')}</p>
                      {Object.keys(fields).map((fieldKey, index) => (
                        <Field
                          component={InputGroup}
                          key={index}
                          name={fieldKey}
                          field={fields[fieldKey]}
                        />
                      ))}
                      <Row>
                        <Col xs="12">
                          <Button
                            color="primary"
                            className="w-100-p"
                            disabled={
                              this.props.pristine || this.props.invalid || this.props.submitting
                            }
                            type="submit"
                          >
                            {getTranslationKey('requestNewPassword')}
                          </Button>
                        </Col>
                      </Row>
                      <Row>
                        <Col className="m-t-10 text-center">
                          <a href="/#/login/">{getTranslationKey('login')}</a>
                        </Col>
                      </Row>
                      {window.USER_TYPE === 'VST' && (
                        <Row className="m-t-20">
                          <Col xs="12">
                            <hr />
                            <a
                              target="_blank"
                              href="https://www.vst-pro.de/impressum/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('imprint')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href="https://www.vst-pro.de/datenschutz/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('privacy')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href="https://www.vst-pro.de/kontakt/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('contact')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href="https://www.vst-pro.de/produktwelten/produkte/produkt/vst-gallerypix/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('productInfos')}
                            </a>
                          </Col>
                        </Row>
                      )}
                      {window.USER_TYPE === 'SB' && (
                        <Row className="m-t-20">
                          <Col xs="12">
                            <hr />
                            <a
                              target="_blank"
                              href="https://scrappbook.de/impressum-2"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('imprint')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href="https://scrappbook.de/datenschutz-2"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('privacy')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href="https://scrappbook.de/kontakt-support/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('contact')}
                            </a>
                          </Col>
                        </Row>
                      )}
                    </Form>
                  </CardBody>
                </Card>
              </CardGroup>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

function validate(values: FormValues): Partial<Record<keyof FormValues, any>> {
  const errors: any = {};

  Object.keys(fields).forEach((fieldKey) => {
    const result = approve.value(values[fieldKey as keyof FormValues], fields[fieldKey].rules);
    if (result.errors.length > 0) {
      errors[fieldKey] = result.errors;
    }
  });

  return errors;
}

interface RootState {
  config: any;
  auth: Auth;
}

const ForgotPasswordForm = reduxForm<FormValues, OwnProps>({
  form: 'forgotPasswordForm',
  validate
})(ForgotPassword);

export default connect(
  (state: RootState) => ({
    config: state.config,
    auth: state.auth
  }),
  actions
)(ForgotPasswordForm);
